import React, { Children, useEffect, useState } from 'react';
import getCurrentActiveFilters from '../../../context/search/helpers/getCurrentActiveFilters';
import useSearch from '../../../context/search/useSearch';
import SearchProductCard from './Components/SearchProductCard';

import { InView, useInView } from 'react-intersection-observer';
import { ISearchAndFilterAdvert, ISearchAndFilterProduct } from '../../../context/search/types';
import LoadingIcon from '../MiniCart/Loading';
import CollectionAdvert from './CollectionAdvert';
import SearchDummyProductCardGrid from './Components/SearchDummyProductCardGrid';

interface PaginationLinkProps {
  index: number
  active: boolean,
  children: React.ReactNode
}

const PaginationNavLink: React.FC<PaginationLinkProps> = ({ active, index, children }): JSX.Element => {
  const { inputEventHandlers } = useSearch()
  const child: any = Children.only(children)
  const childProps = {
    onClick: () => active ? null : inputEventHandlers.handleUpdatePageIndex(index, true, false)
  }
  return React.cloneElement(child, childProps, child.props.children)
}

interface PaginationProps {
  pageCount: number
  pageIndex: number,
}

const PaginationArrowForward: React.FC = (): JSX.Element => (
  <svg width="6" height="9" viewBox="0 0 6 9" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M5.8113 0.283127L9.68833 3.81511C9.68833 3.81567 9.68893 3.81567 9.68953 3.81623C10.1035 4.19374 10.1035 4.80584 9.68953 5.18334L5.8113 8.71645C5.39674 9.09452 4.72443 9.09452 4.31047 8.71645C3.89651 8.33895 3.89651 7.72684 4.31047 7.34878L7.43829 4.49979L4.31047 1.6508C3.89651 1.2733 3.89651 0.660629 4.31047 0.283127C4.72443 -0.0943756 5.39674 -0.0943756 5.8113 0.283127Z" fill="#333333" />
  </svg>
)
const PaginationArrowBack: React.FC = (): JSX.Element => (
  <svg aria-hidden="true" width="6" height="9" viewBox="0 0 6 9" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M4.1887 0.283127L0.311671 3.81511C0.311671 3.81567 0.311069 3.81567 0.310469 3.81623C-0.10349 4.19374 -0.10349 4.80584 0.310469 5.18334L4.1887 8.71645C4.60326 9.09452 5.27557 9.09452 5.68953 8.71645C6.10349 8.33895 6.10349 7.72684 5.68953 7.34878L2.56171 4.49979L5.68953 1.6508C6.10349 1.2733 6.10349 0.660629 5.68953 0.283127C5.27557 -0.0943756 4.60326 -0.0943756 4.1887 0.283127Z" fill="#333333" />
  </svg>
)

const Pagination: React.FC<PaginationProps> = ({ pageCount, pageIndex }): JSX.Element => {
  // Calculate the range of page numbers to display
  const maxPagesToShow = 5;
  let startPage = Math.max(1, pageIndex - Math.floor(maxPagesToShow / 2));
  let endPage = startPage + maxPagesToShow - 1;

  if (endPage > pageCount) {
    endPage = pageCount;
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className={'pagination'}>
      {pageCount > 1 ? (
        <div className="container">
          <ol className='pagination__list'>
            <PaginationNavLink active={pageIndex === 1} index={(pageIndex - 1)}>
              <li className={`pagination__item pagination__item--prev ${pageIndex === 1 ? 'pagination__item pagination__item--prev pagination__item--disabled' : ''}`}>
                <PaginationArrowBack />
                <span className="visually-hidden">Previous page</span>
              </li>
            </PaginationNavLink>
            {pageNumbers.map((pageNum) => {
              const active = (pageIndex === pageNum);
              return (
                <PaginationNavLink active={active} index={pageNum} key={pageNum}>
                  <li className={active ? 'pagination__item pagination__item--active' : 'pagination__item'}>
                    {pageNum}
                  </li>
                </PaginationNavLink>
              )
            })}
            <PaginationNavLink active={pageIndex === pageCount} index={(pageIndex + 1)}>
              <li className={`pagination__item pagination__item--next ${pageIndex === pageCount ? 'pagination__item--disabled' : ''}`}>
                <span className="visually-hidden">Next page</span>
                <PaginationArrowForward />
              </li>
            </PaginationNavLink>
          </ol>
        </div>
      ) : ''}
    </div>)
}

interface InfiniteScrollTriggerProps {
  pageIndex: number
  pageCount: number
  newPageLoading: boolean
  onLoadMore: (newPageIndex: number) => void
}

const InfiniteScrollTrigger: React.FC<InfiniteScrollTriggerProps> = ({ pageIndex, pageCount, newPageLoading, onLoadMore }) => {
  const { inView, ref } = useInView({
    threshold: 0,
    rootMargin: '200px'
  });

  useEffect(() => {
    if (inView && !newPageLoading && pageIndex < pageCount) {
      const newPageIndex = Math.min(pageIndex + 1, pageCount);
      onLoadMore(newPageIndex);
    }
  }, [inView, newPageLoading, pageIndex, pageCount, onLoadMore]);

  return (
    <div className="loader-container" ref={ref}>
      <div className='product-list-preloader' style={{ position: 'relative', top: '-100px' }}></div>
      <div className="loader">
        <span className="loader__icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><path d="M20.201 5.169c-8.254 0-14.946 6.692-14.946 14.946 0 8.255 6.692 14.946 14.946 14.946s14.946-6.691 14.946-14.946c-.001-8.254-6.692-14.946-14.946-14.946zm0 26.58c-6.425 0-11.634-5.208-11.634-11.634 0-6.425 5.209-11.634 11.634-11.634 6.425 0 11.633 5.209 11.633 11.634 0 6.426-5.208 11.634-11.633 11.634z" opacity=".2" /><path d="M26.013 10.047l1.654-2.866a14.855 14.855 0 00-7.466-2.012v3.312c2.119 0 4.1.576 5.812 1.566z"><animateTransform attributeName="transform" attributeType="xml" dur="0.5s" from="0 20 20" repeatCount="indefinite" to="360 20 20" type="rotate" /></path></svg>
        </span>
      </div>
    </div>
  );
};

interface Props {
  dataSet: DOMStringMap
}

const SearchFilteredProducts: React.FC<Props> = ({ dataSet }) => {

  const {
    productSearchResult,
    productSearchResultsState,
    searchFilters,
    config,
    pageIndex,
    filtersLoading,
    newPageLoading,
    inputEventHandlers,
    activeTab,
    searchTerm,
  } = useSearch()

  const [hasInitialInfiniteScrollPosition, setHasInitialInfiniteScrollPosition] = useState(false);

  // Only render if products tab is active
  if (activeTab !== 'products') {
    return null;
  }

  const pageCount = productSearchResult ? Math.ceil(productSearchResult.total / config.rProductsPerPage) : 0;

  const pageToRender = parseInt(dataSet.pageToRender) // the page to render, from the portal, eg for previous pages this is pages up to the requested page
  const requestedPage = parseInt(dataSet.requestedPage) // the page requested by the browser,  in the query string

  if (isNaN(pageToRender) || isNaN(requestedPage)) {
    console.error('Make sure request-page and page-to-render data attrs are on portal')
  }

  const [loadMoreClicked, setLoadMoreClicked] = useState(false)

  useEffect(() => {
    if (!newPageLoading) {
      setLoadMoreClicked(false)
    }
  }, [newPageLoading])

  // For infinite scroll deep links (e.g. ?page=2), scroll to the requested page once products are loaded.
  useEffect(() => {
    if (
      !hasInitialInfiniteScrollPosition &&
      config.rLoadMoreMode === 'infinite' &&
      requestedPage > 1 &&
      pageToRender === requestedPage &&
      pageIndex === requestedPage &&
      productSearchResult
    ) {
      const pageEl = document.querySelector<HTMLElement>(`[data-page-to-render="${requestedPage}"]`);
      if (pageEl) {
        const headerOffset = 120; // approximate header height
        const top = pageEl.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top, behavior: 'auto' });
        setHasInitialInfiniteScrollPosition(true);
      }
    }
  }, [config.rLoadMoreMode, hasInitialInfiniteScrollPosition, pageIndex, pageToRender, productSearchResult, requestedPage])

  // Helper to render a grid of real products
  const renderProductGrid = (products: ISearchAndFilterProduct[]) => (
    <div className={`filter__products ${filtersLoading ? 'is-loading' : ''}`}>
      <InView as="div" className='product-card-container grid-list'>
        {products.map(product => (
          <SearchProductCard product={product} dataSet={dataSet} key={product.index} />
        ))}
      </InView>
    </div>
  );

  // Updated logic for previous pages
  if (pageToRender < requestedPage) {
    const liquidDummies = document.querySelector(`[data-page-to-render="${pageToRender}"] [data-liquid-dummies]`)
    if (liquidDummies) {
      liquidDummies.parentElement.innerHTML = '' // clear liquid dummies
    }
    const pageProducts = productSearchResultsState[pageToRender]?.products;
    if (Array.isArray(pageProducts) && pageProducts.length > 0) {
      return renderProductGrid(pageProducts);
    } else {
      // Render dummy grid with eager fetch trigger
      return (
        <div className={`filter__products ${filtersLoading ? 'is-loading' : ''}`}>
          <InView
            as="div"
            threshold={0}
            triggerOnce={true}
            onChange={(inView) => {
              if (inView && !newPageLoading && !productSearchResultsState[pageToRender]) {
                console.info(`[Search] Eager fetching page ${pageToRender} (cache miss)`);
                inputEventHandlers.handleUpdatePageIndex(pageToRender, true, true);
              }
            }}
            className='product-card-container grid-list'
          >
            <SearchDummyProductCardGrid count={config.rProductsPerPage} />
          </InView>
        </div>
      );
    }
  }

  // Main render block
  if (!searchFilters) {
    return null;
  }

  const collectionAdverts = (window as any).adverts ? (window as any).adverts : null;

  const selectedFilters = getCurrentActiveFilters(searchFilters);
  if ((!selectedFilters || selectedFilters.length < 1) && !productSearchResult) {
    return null;
  }

  let productsToRender = [] as ISearchAndFilterProduct[][]

  if (config.rLoadMoreMode === 'loadMore' || config.rLoadMoreMode === 'infinite') {
    if (pageToRender === requestedPage) {
      //everything after requested page
      productsToRender = productSearchResultsState.slice(requestedPage).map(results => results?.products)
    } else if (pageToRender < requestedPage) {
      const liquidDummies = document.querySelector(`[data-page-to-render="${pageToRender}"] [data-liquid-dummies]`)
      if (liquidDummies) {
        liquidDummies.parentElement.innerHTML = '' // clear liquid dummies
      }

      const pageProducts = productSearchResultsState[pageToRender]?.products;
      if (Array.isArray(pageProducts) && pageProducts.length > 0) {
        return (
          <div className={`filter__products ${filtersLoading ? 'is-loading' : ''}`}>
            <InView as="div" className='product-card-container grid-list'>
              {pageProducts.map(product => (
                <SearchProductCard product={product} dataSet={dataSet} key={product.index} />
              ))}
            </InView>
          </div>
        );
      } else {
        // fallback: render dummy products in react land
        const liquidDummies = document.querySelector(`[data-page-to-render="${pageToRender}"] [data-liquid-dummies]`)
        if (liquidDummies) {
          liquidDummies.parentElement.innerHTML = '' // clear liquid dummies
        }
        return (
          <div className={`filter__products ${filtersLoading ? 'is-loading' : ''}`}>
            <InView as="div" onChange={(inView) => {
              if (newPageLoading) return
              const newPageIndex = inView ? pageToRender : null;
              if (newPageIndex && newPageIndex !== pageIndex) {
                inputEventHandlers.handleUpdatePageIndex(newPageIndex, true, false)
              }
            }} className='product-card-container grid-list'>
              <SearchDummyProductCardGrid count={config.rProductsPerPage} />
            </InView>
          </div>
        )
      }
    }
  } else {
    // needs to be an empty array if not found (undefined within an array makes the length 1 :facepalm: )
    productsToRender = productSearchResult?.products ? [productSearchResult.products] : []
  }

  if (productsToRender.length < 1 && pageToRender < requestedPage) {

    const liquidDummies = document.querySelector(`[data-page-to-render="${pageToRender}"] [data-liquid-dummies]`)
    if (liquidDummies) {
      liquidDummies.parentElement.innerHTML = '' // clear liquid dummies
    }
    return (
      //render dummy products in react land
      <div className={`filter__products ${filtersLoading ? 'is-loading' : ''}`}>
        <InView as="div" onChange={(inView) => {
          if (newPageLoading) return
          const newPageIndex = inView ? pageToRender : null;
          if (newPageIndex && newPageIndex !== pageIndex) {
            inputEventHandlers.handleUpdatePageIndex(newPageIndex, true, false)
          }
        }} className='product-card-container grid-list'>
          <SearchDummyProductCardGrid count={config.rProductsPerPage} />
        </InView>
      </div>
    )
  }

  if (productsToRender.length > 0) {
    if (!productsToRender[0] || (productsToRender[0] && productsToRender[0].length === 0)) {
      return <div className="search__results-no-results">
        <p>Sorry we couldn&lsquo;t find &quot;{searchTerm}&quot;</p>
        <p>Try adjusting your search or try one of these suggestions:</p>
        <div className="search__results-no-results-buttons">
          <a className="btn-b-to-w btn--large" href="/collections/clothing">Shop All Women&apos;s</a>
          <a className="btn-2 btn--secondary btn--large" href="/">Back Home</a>
        </div>
      </div>;
    } else {
      // Compute how many products are visible and, for pagination, the X–Y range.
      let currentlyShown = 0;
      let firstProduct = 0;
      let lastProduct = 0;
      const totalProducts = productSearchResult?.total || 0;
      const isPaginationMode = config.rLoadMoreMode === 'paginate';
      const requestedPageNumber = isPaginationMode
        ? (pageIndex || requestedPage || 1)
        : (requestedPage || pageIndex || 1);

      if (isPaginationMode) {
        // Numbered pagination: show fixed X–Y range for the requested page.
        firstProduct = (requestedPageNumber - 1) * config.rProductsPerPage + 1;
        lastProduct = Math.min(requestedPageNumber * config.rProductsPerPage, totalProducts);
        currentlyShown = productSearchResult?.products?.length || 0;
      } else if (config.rLoadMoreMode === 'loadMore') {

        const visibleProductsCount = productsToRender
          .reduce((sum, page) => sum + (Array.isArray(page) ? page.length : 0), 0);

        firstProduct = (requestedPageNumber - 1) * config.rProductsPerPage + 1;

        if (visibleProductsCount > 0) {
          lastProduct = Math.min(firstProduct + visibleProductsCount - 1, totalProducts);
          currentlyShown = visibleProductsCount;
        } else {
          lastProduct = Math.min(requestedPageNumber * config.rProductsPerPage, totalProducts);
          currentlyShown = Math.max(0, lastProduct);
        }
      } else if (config.rLoadMoreMode === 'infinite') {
        const visibleProductsCount = productsToRender
          .reduce((sum, page) => sum + (Array.isArray(page) ? page.length : 0), 0);

        if (pageIndex === pageCount && totalProducts > 0) {
          currentlyShown = totalProducts;
        } else if (visibleProductsCount > 0) {
          const fromRequestedPage = (requestedPageNumber - 1) * config.rProductsPerPage + visibleProductsCount;
          currentlyShown = Math.min(fromRequestedPage, totalProducts);
        } else {
          const productsLoaded = productSearchResult?.products?.length || 0;
          currentlyShown = Math.min(productsLoaded, totalProducts);
        }
      } else {
        currentlyShown = productSearchResult?.products?.length || 0;
      }

      const ProductCountDisplay = () => {
        // Toggle comes from theme setting via data-show-product-count.
        const showProductCountSetting = dataSet.showProductCount;
        const showProductCount = showProductCountSetting !== 'false';

        if (!showProductCount || totalProducts === 0 || pageToRender !== requestedPage) {
          return null;
        }

        let displayText = '';
        const isRangeMode = isPaginationMode || config.rLoadMoreMode === 'loadMore';
        if (isRangeMode && firstProduct > 0 && lastProduct > 0) {
          displayText = `Showing ${firstProduct}-${lastProduct} of ${totalProducts} products`;
        } else {
          displayText = `Showing ${currentlyShown} of ${totalProducts} products`;
        }

        return (
          <div className="collection-product-count">
            <p className="collection-product-count__text">
              {displayText}
            </p>
          </div>
        );
      };

      return (
        <div className={`filter__products ${filtersLoading ? 'is-loading' : ''}`}>
          {productsToRender.map((page, index) => (
            <InView key={index} as="div" onChange={(inView) => {
              if (config.rLoadMoreMode === 'paginate' || newPageLoading) return
              const newPageIndex = inView ? index + pageToRender : null;
              if (newPageIndex && newPageIndex !== pageIndex) {
                inputEventHandlers.handleUpdatePageIndex(newPageIndex, false, false)
              }
            }} className="product-card-container grid-list">
              {page.map(product => {
                if (collectionAdverts) {
                  const advertToRender = Object.values(collectionAdverts).find((advert) =>
                    product.pageIndex === 1 && product.index === (advert as ISearchAndFilterAdvert).position - 1
                  ) as ISearchAndFilterAdvert | undefined;
                  return (
                    <React.Fragment key={product.index}>
                      {advertToRender && !selectedFilters.length ? <CollectionAdvert advert={advertToRender} /> : ''}
                      <SearchProductCard product={product} dataSet={dataSet} />
                    </React.Fragment>
                  );
                } else {
                  return (
                    <SearchProductCard key={product.index} product={product} dataSet={dataSet} />
                  )
                }
              })}
            </InView>
          ))}
          {config.rLoadMoreMode === 'paginate' && (
            <>
              <ProductCountDisplay />
              <Pagination pageCount={pageCount} pageIndex={pageIndex} />
            </>
          )}
          {config.rLoadMoreMode === 'loadMore' && pageToRender === requestedPage && (
            <>
              <ProductCountDisplay />
              {pageIndex < pageCount && (
                <div className="grid-item grid-item--full-width">
                  {loadMoreClicked ?
                    <div className="loader loader-container">
                      <span className="loader__icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><path d="M20.201 5.169c-8.254 0-14.946 6.692-14.946 14.946 0 8.255 6.692 14.946 14.946 14.946s14.946-6.691 14.946-14.946c-.001-8.254-6.692-14.946-14.946-14.946zm0 26.58c-6.425 0-11.634-5.208-11.634-11.634 0-6.425 5.209-11.634 11.634-11.634 6.425 0 11.633 5.209 11.633 11.634 0 6.426-5.208 11.634-11.633 11.634z" opacity=".2" /><path d="M26.013 10.047l1.654-2.866a14.855 14.855 0 00-7.466-2.012v3.312c2.119 0 4.1.576 5.812 1.566z"><animateTransform attributeName="transform" attributeType="xml" dur="0.5s" from="0 20 20" repeatCount="indefinite" to="360 20 20" type="rotate" /></path></svg>
                      </span>
                    </div> : <button className='btn btn--primary btn--center btn--test' type='button' onClick={
                      () => {
                        // Classes on the button need to match in loadMore.js line 103(ish)
                        if (newPageLoading) return
                        setLoadMoreClicked(true)
                        const newPageIndex = Math.min(pageIndex + 1, pageCount);
                        inputEventHandlers.handleUpdatePageIndex(newPageIndex, true, false);

                      }}>Load More</button>}
                </div>
              )}
            </>
          )}
          {config.rLoadMoreMode === 'infinite' && pageToRender === requestedPage && (
            <>
              <ProductCountDisplay />
              {pageIndex < pageCount && (
                <InfiniteScrollTrigger
                  pageIndex={pageIndex}
                  pageCount={pageCount}
                  newPageLoading={newPageLoading}
                  onLoadMore={(newPageIndex) => inputEventHandlers.handleUpdatePageIndex(newPageIndex, true, false)}
                />
              )}
            </>
          )}
        </div>
      );
    }
  }

  // fallback for loading
  return (
    <div className='filter__loading'>
      <LoadingIcon />
    </div>
  );
}

export default SearchFilteredProducts
